import { getEnvironment, getHiddenDataString } from '../hidden-data-provider';

// this error is printed when google recaptcha script is not added to the master page,
// site key is wrong in case of race condition between google and waves scripts
const NOT_DEFINED_ERR = 'Recaptcha is not defined.';

// this error is printed when the hidden div with recaptcha key is missing
const SITE_KEY_ERR = 'Recaptcha site key is not printed in HTML.';

// ------------------------- Recaptcha v3 --------------------------------------------

/**
 * returns recaptcha site ket by its version
 */
export const getRecaptchaSiteKey = (version = 3) => {

    const env = getEnvironment();

    // recaptcha for automation
    // https://wavesaudio.atlassian.net/browse/DSWEB-540
    if(
        version !== 3 &&
        (env === 'dev' || env === 'test') &&
        navigator.userAgent &&
        navigator.userAgent.trim().startsWith('WavesSelenium')){
        return getHiddenDataString('recaptcha2-automation');
    }

    // The recaptcha code is taken from the following hidden DIVs:
    // <div id="recaptcha3" className="hidden">6LeG-IcUAAAAAIb42eguHdUztlZNcrLPhuk81WSM</div>
    // <div id="recaptcha2" className="hidden">6LftoDMUAAAAAC9QpaYXNehGHSu__XxRJyFAIgkJ</div>
    return getHiddenDataString(`recaptcha${version}`);
}

/**
 * recaptcha v3 should be executed on each action that should be protected
 * the following script should be added to each page:
 * <script src="https://www.google.com/recaptcha/api.js?render=reCAPTCHA_site_key_v3"></script>
 * reCAPTCHA tokens expire after two minutes, make sure to call execute when the user takes the action rather than on page load
 * also we should ensure that grecaptcha.ready callback happened
 *
 * Usage:
 * await executeRecaptcha3()
 */
export const executeRecaptcha3 = () => {

    return new Promise((resolve, reject) => {

        if (!window.grecaptcha) {
            reject(NOT_DEFINED_ERR);
            return;
        }

        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(getRecaptchaSiteKey(3), {action: 'submit'}).then(token => {
                resolve(token);
            });
        });
    });
};

/**
 * get recaptcha token
 * @param {number=} version of recaptcha
 * @return {string}
 */
export const getRecaptchaToken = async (version = 3) => {
    return version === 3 ? await executeRecaptcha3() : window.grecaptcha.getResponse();
};

// ------------------------- Recaptcha v2 --------------------------------------------

/**
 * render recaptcha V2
 * containerID - the ID of an element that will contain recaptcha 2 HTML
 */
export const renderRecaptcha2 = (containerID: string) : Promise<number> => {

    return new Promise((resolve, reject) => {

        if(!window.grecaptcha){
            reject(NOT_DEFINED_ERR);
            return;
        }

        const siteKeyV2 = getRecaptchaSiteKey(2);

        if(!siteKeyV2){
            reject(SITE_KEY_ERR);
            return;
        }

        window.grecaptcha.ready(() => {

            const widgetID = window.grecaptcha.render(containerID, {
                'sitekey': siteKeyV2,
                //'badge': 'inline', //bottomright, bottomleft, inline
                //'size': 'invisible' //compact, normal, invisible
            });

            resolve(widgetID);
        });
    });
};

/**
 * reset recaptcha V2
 */
export const resetRecaptcha2 = () => {
    if(!window.grecaptcha) return;
    window.grecaptcha.reset();
};


/**
 * recaptcha 2 client side error
 */
export const getRecaptcha2Error = () => {
    return 'Please select the "I\'m not a robot" checkbox.';
};